import {
    useState, useMemo, useEffect, useCallback
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import Empty from 'constants/empty';
import DeliveryFrequencyUtils from 'utils/DeliveryFrequency';
import LanguageLocale from 'utils/LanguageLocale';
import OrderUtils from 'utils/Order';
import FormValidator from 'utils/FormValidator';
import DateUtils from 'utils/Date';
import CreditCardUtils from 'utils/CreditCard';
import HelperUtils from 'utils/Helpers';
import { sanitizeHtml } from 'utils/sanitize';
import AutoReplenishmentBindings from 'analytics/bindingMethods/pages/myAccount/AutoReplenishmentBindings';
import autoReplenishmentActions from 'actions/AutoReplenishmentActions';
import ShippingAndPaymentInfoSelector from 'selectors/page/autoReplenishment/shippingAndPaymentInfo/shippingAndPaymentInfoSelector';
import { userSelector } from 'selectors/user/userSelector';
import { getFeatureFlags } from 'components/RichProfile/MyAccount/AutoReplenishment/utils/featureFlags';
import { formatPrice, getSavingAmount } from 'components/RichProfile/MyAccount/AutoReplenishment/utils/price';
import ManageSubscriptionModalEN from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_en_US';
import ManageSubscriptionModalFR from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_fr_CA';

const { capitalizeFirstLetter } = HelperUtils;
const { formatFrequencyType } = DeliveryFrequencyUtils;
const { getLocalisedDataFromResource } = LanguageLocale;
const { shippingAndPaymentInfoSelector } = ShippingAndPaymentInfoSelector;

const PAYMENTS_IMAGE_FOLDER = '/img/ufe/payments';

const getText = getLocalisedDataFromResource(ManageSubscriptionModalEN, ManageSubscriptionModalFR);

/**
 * Transforms payment data into credit card display information
 * @param {Object} payment - Payment information from API
 * @returns {Object} Formatted credit card data or empty object
 */
const transformCreditCardInfo = payment => {
    if (!payment?.cardType) {
        return Empty.Object;
    }

    const cardType = payment.cardType || Empty.String;
    const cardImage = OrderUtils.getThirdPartyCreditCard({ cardType }) || 'placeholder';
    const label = CreditCardUtils.getCardLabel(cardType);

    return {
        hasData: true,
        expDate: `${payment.expirationMonth || Empty.String}/${payment.expirationYear || Empty.String}`,
        logo: `${PAYMENTS_IMAGE_FOLDER}/${cardImage}.svg`,
        title: `${cardType} *${payment.cardTokenNumber || Empty.String}`,
        label: `${label} *${payment.cardTokenNumber || Empty.String}`,
        alt: label
    };
};

/**
 * Transforms shipping address data into display information
 * @param {Object} shippingAddress - Shipping address from API
 * @returns {Object} Formatted shipping info or empty object
 */
const transformShippingInfo = shippingAddress => {
    if (!shippingAddress?.firstName) {
        return Empty.Object;
    }

    const {
        firstName = Empty.String,
        lastName = Empty.String,
        address1 = Empty.String,
        city = Empty.String,
        state = Empty.String,
        zipcode = Empty.String,
        postalCode = Empty.String,
        phoneNumber = Empty.String
    } = shippingAddress;

    return {
        hasData: true,
        addressLine1: address1,
        phoneNumber: FormValidator.getFormattedPhoneNumber(phoneNumber),
        fullName: `${firstName} ${lastName}`,
        fullAddress: `${city}, ${state} ${zipcode || postalCode}`
    };
};

/**
 * Custom hook for managing subscription modal state and operations
 */
export function useManageSubscriptionModal({ currentSubscription, onDismiss, onResume, fireGenericErrorAnalytics }) {
    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useDispatch();

    const shippingAndPaymentData = useSelector(shippingAndPaymentInfoSelector, shallowEqual);
    const firstName = useSelector(state => userSelector(state).firstName);

    const { frequency, frequencyType, items = Empty.Array } = currentSubscription || Empty.Object;
    const product = items?.[0] || Empty.Object;

    const featureFlags = useMemo(getFeatureFlags, []);

    const localization = useMemo(() => {
        const frequencyText = `${frequency} ${formatFrequencyType(frequency, frequencyType)}`;

        return {
            askToUpdateDelivery: getText('askToUpdateDelivery'),
            cancel: getText('cancel'),
            cancelSubscription: getText('cancelSubscription'),
            confirmUpdate: getText('confirmUpdate'),
            changeDate: getText('changeDate'),
            creditCardExpDate: getText('creditCardExpDate'),
            deliveryEvery: getText('deliveryEvery'),
            deliveryTitle: getText('deliveryTitle'),
            deliveryValue: getText('deliveryValue', [frequencyText]),
            itemQuantityText: `${getText('qty')} : ${product.qty || 0}`,
            itemText: getText('item', [product.skuId]),
            legalCopy: getText('legalCopy'),
            manageSubscription: getText('manageSubscription'),
            nextShipment: getText('nextShipment'),
            pause: getText('pause'),
            paymentTitle: getText('paymentMethod'),
            qty: getText('qty'),
            selectAddress: getText('selectAddress'),
            selectPayment: getText('selectPayment'),
            shippingTitle: getText('shippingAddress'),
            skip: getText('skip'),
            skuSwapButton: getText('skuSwapButton'),
            skuSwapUnavailable: getText('skuSwapUnavailable'),
            tabHeadingText: `${getText('deliveryEvery')}: ${frequencyText}`,
            resumeModalTitle: getText('resumeModalTitle'),
            resumeSubmitButtonText: getText('resumeSubmitButtonText'),
            resumeSuccessButtonText: getText('resumeSuccessButtonText')
        };
    }, [product, frequency, frequencyType]);

    const productData = useMemo(
        () => ({
            ...product,
            productPrice: formatPrice(product),
            savingAmount: getSavingAmount(product, featureFlags.isFlexIncentivesEnabled)
        }),
        [product, featureFlags.isFlexIncentivesEnabled]
    );

    const savingsCopy = useMemo(() => {
        const name = capitalizeFirstLetter(sanitizeHtml(firstName));
        const discount = sanitizeHtml(product.acceleratedPromotion?.remainingAcceleratedDiscount);
        const date = sanitizeHtml(DateUtils.getDateInMMDDYYYYShortMonth(product.acceleratedPromotion?.promoExpirationDate));
        const yearlySavings = sanitizeHtml(product.yearlySavings);

        return {
            annuallySaving: getText('annuallySaving', [name, yearlySavings]),
            hotDealSaving: getText('hotDealSaving', [name, discount, date])
        };
    }, [firstName, product.yearlySavings, product.acceleratedPromotion]);

    const shippingPayment = useMemo(() => {
        const creditCard = transformCreditCardInfo(shippingAndPaymentData?.payment);
        const shippingInfo = transformShippingInfo(shippingAndPaymentData?.shippingAddress);

        return {
            creditCard,
            shippingInfo: {
                ...shippingInfo,
                nextShipmentDate: currentSubscription.nextScheduleRunDate
                    ? DateUtils.getPromiseDate(`${currentSubscription.nextScheduleRunDate}T00:00`)
                    : '-'
            }
        };
    }, [shippingAndPaymentData, currentSubscription]);

    const shouldShowItemVariation = !!(product?.variationType && product?.variationValue && product?.skuId);
    const shouldShowDeliveryMessage = frequency > 1;

    /**
     * Load shipping and payment information when the modal opens
     * Set isLoading to true initially and false once data is loaded or fails
     */
    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            const { paymentId, profileId, shippingAddressId } = currentSubscription;
            const action = autoReplenishmentActions.loadShippingAndPaymentInfo(
                profileId,
                shippingAddressId,
                paymentId,
                onDismiss,
                fireGenericErrorAnalytics
            );

            try {
                await dispatch(action);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [currentSubscription, onDismiss, fireGenericErrorAnalytics, dispatch]);

    const onModalClose = useCallback(() => {
        if (!onResume) {
            AutoReplenishmentBindings.manageSubscriptionClose(currentSubscription);
        }

        onDismiss();
    }, [currentSubscription, onDismiss, onResume]);

    return {
        ...shippingPayment,
        featureFlags,
        isLoading,
        localization,
        onModalClose,
        product: productData,
        savingsCopy,
        shouldShowDeliveryMessage,
        shouldShowItemVariation
    };
}
